import { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ESTILO_INPUT_GENERICO } from "../../../utils/const/estilos-const";
import "../../../style/style-cargando.css";
import { crearProducto, modificarProducto } from "../../../api/api-producto";
import ArrowBack from "../../../components/arrow-back";
import { useTranslation } from "react-i18next";
import { sharedApiErrorMessage, sharedNextPage } from "../../../utils/shared-functions";
import { useProcessing, useToast } from "../../../utils/context/ui-feedback-context";
import { FormActionsBar, FormCancelButton, FormSubmitButton } from "../../../components/form-actions-component";
import FormSelectField from "../../../components/product/FormSelectField";
import ProductMainImageSection from "../../../components/product/ProductMainImageSection";
import ProductGallerySection from "../../../components/product/ProductGallerySection";
import { obtenerListaCategoriaProducto } from "../../../api/api-categoria";
import { obtenerListaMoneda } from "../../../api/api-moneda";
import { obtenerListaBadge } from "../../../api/api-badge";
import cargandoCard from "../../../components/cargando-card";
import {
  calculatePrecioConDescuento,
  createEmptyProductoForm,
  findSelectOption,
  parseImagenesField,
} from "../../../utils/product/producto-form-utils";

const PUBLICO_OPTIONS = [
  { value: 1, labelKey: "label_publico_si" },
  { value: 0, labelKey: "label_publico_no" },
];

export default function ProductoPage() {
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();

  const [cargandoPage, setCargandoPage] = useState(true);
  const [formData, setFormData] = useState(createEmptyProductoForm());
  const [titulo, setTitulo] = useState("");
  const [sendRequest, setSendRequest] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [showAdvancedImages, setShowAdvancedImages] = useState(false);

  const [listaCategoria, setListaCategoria] = useState([]);
  const [categoriaSelected, setCategoriaSelected] = useState(null);
  const [listaMoneda, setListaMoneda] = useState([]);
  const [monedaSelected, setMonedaSelected] = useState(null);
  const [listaBadge, setListaBadge] = useState([]);
  const [badgeSelected, setBadgeSelected] = useState(null);
  const [publicoSelected, setPublicoSelected] = useState(null);

  const { showToast } = useToast();
  const { showProcessing, hideProcessing } = useProcessing();

  const charLimits = {
    slug: 1024,
    codigo: 128,
    nombre: 1024,
    descripcion: 2048,
  };

  const publicoOptions = PUBLICO_OPTIONS.map((item) => ({
    value: item.value,
    label: t(item.labelKey),
  }));

  useEffect(() => {
    iniciar();
  }, []);

  const syncSelectsFromForm = (params, categorias, monedas, badges) => {
    setCategoriaSelected(findSelectOption(categorias, params.idCategoriaProducto));
    setMonedaSelected(findSelectOption(monedas, params.idMoneda));
    setBadgeSelected(findSelectOption(badges, params.idBadge));
    setPublicoSelected(
      publicoOptions.find((item) => item.value === Number(params.publico)) ?? publicoOptions[0]
    );
  };

  const iniciar = async () => {
    try {
      const data = location.state;
      if (!data?.params) {
        sharedNextPage("lista-producto", {}, history);
        return;
      }

      const emptyForm = createEmptyProductoForm();
      const params = {
        ...emptyForm,
        ...data.params,
        tipoImagenPrincipal:
          data.params.tipoImagenPrincipal || emptyForm.tipoImagenPrincipal,
        tipoImagenes: data.params.tipoImagenes || emptyForm.tipoImagenes,
        imagenes: parseImagenesField(data.params.imagenes),
      };

      setTitulo(data.titulo ?? "");
      setFormData(params);

      const [responseCategorias, responseMoneda, responseBadge] = await Promise.all([
        obtenerListaCategoriaProducto(true, t),
        obtenerListaMoneda(true, t),
        obtenerListaBadge(true, t),
      ]);

      if (responseCategorias.error) {
        showToast(sharedApiErrorMessage(responseCategorias.message, t("alert_process_failed")), "error");
      } else {
        setListaCategoria(responseCategorias.body ?? []);
      }

      if (responseMoneda.error) {
        showToast(sharedApiErrorMessage(responseMoneda.message, t("alert_process_failed")), "error");
      } else {
        setListaMoneda(responseMoneda.body ?? []);
      }

      if (responseBadge.error) {
        showToast(sharedApiErrorMessage(responseBadge.message, t("alert_process_failed")), "error");
      } else {
        setListaBadge(responseBadge.body ?? []);
      }

      syncSelectsFromForm(
        params,
        responseCategorias.body ?? [],
        responseMoneda.body ?? [],
        responseBadge.body ?? []
      );
    } catch (err) {
      console.log("iniciar err", err);
      showToast(t("alert_process_failed"), "error");
    } finally {
      setCargandoPage(false);
    }
  };

  const updateFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const limit = charLimits[name];
    if (limit == null || value.length <= limit) {
      updateFormField(name, value);
    }
  };

  const handleRatingChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^[1-5]$/.test(value)) {
      updateFormField("rating", value === "" ? "" : Number(value));
    }
  };

  const handleIntegerChange = (e) => {
    const { name, value } = e.target;
    if (value === "" || /^\d+$/.test(value)) {
      updateFormField(name, value === "" ? "" : Number(value));
    }
  };

  const applyPrecioCalculado = (prev, updates) => {
    const next = { ...prev, ...updates };
    const calculated = calculatePrecioConDescuento(
      next.precioOriginal,
      next.porcentajeDescuento
    );
    if (calculated != null) {
      next.precio = calculated;
    }
    return next;
  };

  const handlePrecioOriginalChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setFormData((prev) => applyPrecioCalculado(prev, { precioOriginal: value }));
    }
  };

  const handlePorcentajeDescuentoChange = (e) => {
    const { value } = e.target;
    if (value === "") {
      setFormData((prev) => applyPrecioCalculado(prev, { porcentajeDescuento: 0 }));
      return;
    }
    if (!/^\d+$/.test(value)) return;
    const porcentajeDescuento = Number(value);
    if (porcentajeDescuento > 100) return;
    setFormData((prev) => applyPrecioCalculado(prev, { porcentajeDescuento }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      updateFormField(name, value);
    }
  };

  const handleCancelClick = () => {
    history.goBack();
  };

  const validarFormulario = () => {
    let message = "";

    if (!categoriaSelected?.value) {
      message += `${t("alert_debe_seleccionar_categoria")}\n`;
    }
    if (String(formData.nombre).trim().length === 0) {
      message += `${t("alert_debe_ingresar_nombre")}\n`;
    }
    if (String(formData.descripcion).trim().length === 0) {
      message += `${t("alert_debe_ingresar_descripcion")}\n`;
    }
    if (!monedaSelected?.value) {
      message += `${t("alert_debe_seleccionar_moneda")}\n`;
    }
    if (String(formData.precio).trim().length === 0) {
      message += `${t("alert_debe_ingresar_precio")}\n`;
    }
    if (parseImagenesField(formData.imagenes).length === 0) {
      message += `${t("alert_debe_agregar_al_menos_una_imagen")}\n`;
    }

    if (message.length > 0) {
      showToast(message);
      return false;
    }
    return true;
  };

  const buildPayload = () => ({
    id: formData.id,
    idCategoriaProducto: categoriaSelected?.value ?? "",
    slug: formData.slug,
    codigo: formData.codigo,
    nombre: formData.nombre,
    descripcion: formData.descripcion,
    precioOriginal: Number(formData.precioOriginal || 0),
    porcentajeDescuento: Number(formData.porcentajeDescuento || 0),
    precio: Number(formData.precio || 0),
    idMoneda: monedaSelected?.value ?? "",
    tipoImagenPrincipal: formData.tipoImagenPrincipal,
    imagenPrincipal: formData.imagenPrincipal,
    tipoImagenes: formData.tipoImagenes,
    imagenes: parseImagenesField(formData.imagenes),
    rating:
      formData.rating >= 1 && formData.rating <= 5 ? Number(formData.rating) : 0,
    cantidadDisponiblePublico: Number(formData.cantidadDisponiblePublico || 0),
    cantidadDisponiblePrivado: Number(formData.cantidadDisponiblePrivado || 0),
    idBadge: badgeSelected?.value ?? null,
    publico: publicoSelected?.value ?? 1,
    habilitado: formData.habilitado ?? 1,
  });

  const handleSaveClick = async () => {
    if (!validarFormulario()) return;

    try {
      const objEnviar = buildPayload();
      // console.log("objEnviar", objEnviar);
      // return;
      showProcessing();
      setSendRequest(true);
      const response = formData.id === 0
        ? await crearProducto(objEnviar)
        : await modificarProducto(objEnviar);

      if (response.error === false) {
        showToast(
          t(formData.id === 0 ? "alert_se_creo_correctamente" : "alert_se_modifico_correctamente"),
          "success"
        );
        handleCancelClick();
        return;
      }

      showToast(
        sharedApiErrorMessage(response.message, t("alert_process_failed")),
        "error",
        3000
      );
    } catch (error) {
      console.log("handleSaveClick error", error);
      showToast(t("alert_process_failed"), "error");
    } finally {
      hideProcessing();
      setSendRequest(false);
    }
  };

  const isBusy = sendRequest || imageBusy;

  if (cargandoPage) {
    return cargandoCard();
  }

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-6 pb-8">
      <div className="bg-white py-6 shadow-md rounded-lg sm:px-4 lg:px-6 mt-4">
        <div className="flex items-center gap-2 mb-6">
          <ArrowBack onClick={handleCancelClick} />
          <h2 className="text-xl font-bold text-gray-900">{titulo}</h2>
        </div>

        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6 lg:col-span-4">
            <FormSelectField
              htmlFor="categoria"
              label={t("label_categoria")}
              required
              options={listaCategoria}
              value={categoriaSelected}
              onChange={setCategoriaSelected}
              placeholder={t("placeholder_seleccione_categoria")}
              isDisabled={isBusy}
            />
          </div>

          <div className="col-span-12 md:col-span-6 lg:col-span-4">
            <FormSelectField
              htmlFor="moneda"
              label={t("label_moneda")}
              required
              options={listaMoneda}
              value={monedaSelected}
              onChange={setMonedaSelected}
              placeholder={t("placeholder_seleccione_moneda")}
              isDisabled={isBusy}
            />
          </div>

          <div className="col-span-12 md:col-span-6 lg:col-span-4">
            <FormSelectField
              htmlFor="badge"
              label={t("label_badge")}
              options={listaBadge}
              value={badgeSelected}
              onChange={setBadgeSelected}
              placeholder={t("placeholder_seleccione_badge")}
              isDisabled={isBusy}
            />
          </div>

          <div className="col-span-12 md:col-span-6 lg:col-span-4">
            <label htmlFor="codigo" className="block text-md font-medium text-gray-700 mb-2">
              {t("label_codigo")}
            </label>
            <input
              id="codigo"
              name="codigo"
              type="text"
              value={formData.codigo ?? ""}
              onChange={handleInputChange}
              className={ESTILO_INPUT_GENERICO}
              maxLength={charLimits.codigo}
              placeholder={t("placeholder_ingresa_codigo")}
              disabled={isBusy}
            />
            <p className="text-sm text-gray-500 flex justify-end mt-1">
              {(formData.codigo ?? "").length}/{charLimits.codigo}
            </p>
          </div>

          <div className="col-span-12 md:col-span-6 lg:col-span-4">
            <label htmlFor="slug" className="block text-md font-medium text-gray-700 mb-2">
              {t("label_slug")}
            </label>
            <input
              id="slug"
              name="slug"
              type="text"
              value={formData.slug ?? ""}
              onChange={handleInputChange}
              className={ESTILO_INPUT_GENERICO}
              maxLength={charLimits.slug}
              placeholder={t("placeholder_ingresa_slug")}
              disabled={isBusy}
            />
            <p className="text-sm text-gray-500 flex justify-end mt-1">
              {(formData.slug ?? "").length}/{charLimits.slug}
            </p>
          </div>

          <div className="col-span-12 md:col-span-6 lg:col-span-4">
            <FormSelectField
              htmlFor="publico"
              label={t("label_publico")}
              options={publicoOptions}
              value={publicoSelected}
              onChange={setPublicoSelected}
              placeholder={t("placeholder_seleccione_estado")}
              isClearable={false}
              isDisabled={isBusy}
            />
          </div>

          <div className="col-span-12">
            <label htmlFor="nombre" className="block text-md font-medium text-gray-700 mb-2">
              {t("label_nombre")}<span className="text-red-500"> *</span>
            </label>
            <input
              id="nombre"
              name="nombre"
              type="text"
              value={formData.nombre ?? ""}
              onChange={handleInputChange}
              className={ESTILO_INPUT_GENERICO}
              maxLength={charLimits.nombre}
              placeholder={t("placeholder_ingresa_nombre")}
              disabled={isBusy}
            />
            <p className="text-sm text-gray-500 flex justify-end mt-1">
              {(formData.nombre ?? "").length}/{charLimits.nombre}
            </p>
          </div>

          <div className="col-span-12">
            <label htmlFor="descripcion" className="block text-md font-medium text-gray-700 mb-2">
              {t("label_descripcion")}<span className="text-red-500"> *</span>
            </label>
            <textarea
              id="descripcion"
              name="descripcion"
              value={formData.descripcion ?? ""}
              onChange={handleInputChange}
              className={ESTILO_INPUT_GENERICO}
              maxLength={charLimits.descripcion}
              placeholder={t("placeholder_ingresa_descripcion")}
              rows={4}
              disabled={isBusy}
            />
            <p className="text-sm text-gray-500 flex justify-end mt-1">
              {(formData.descripcion ?? "").length}/{charLimits.descripcion}
            </p>
          </div>

          <div className="col-span-12 md:col-span-4">
            <label htmlFor="precioOriginal" className="block text-md font-medium text-gray-700 mb-2">
              {t("label_precio_original")}
            </label>
            <input
              id="precioOriginal"
              name="precioOriginal"
              type="text"
              inputMode="decimal"
              value={formData.precioOriginal ?? ""}
              onChange={handlePrecioOriginalChange}
              className={ESTILO_INPUT_GENERICO}
              disabled={isBusy}
            />
          </div>

          <div className="col-span-12 md:col-span-4">
            <label htmlFor="porcentajeDescuento" className="block text-md font-medium text-gray-700 mb-2">
              {t("label_porcentaje_descuento")}
            </label>
            <input
              id="porcentajeDescuento"
              name="porcentajeDescuento"
              type="text"
              inputMode="numeric"
              maxLength={3}
              value={formData.porcentajeDescuento ?? 0}
              onChange={handlePorcentajeDescuentoChange}
              className={ESTILO_INPUT_GENERICO}
              disabled={isBusy}
            />
          </div>

          <div className="col-span-12 md:col-span-4">
            <label htmlFor="precio" className="block text-md font-medium text-gray-700 mb-2">
              {t("label_precio")}<span className="text-red-500"> *</span>
            </label>
            <input
              id="precio"
              name="precio"
              type="text"
              inputMode="decimal"
              value={formData.precio ?? ""}
              onChange={handleNumberChange}
              className={ESTILO_INPUT_GENERICO}
              disabled={isBusy}
            />
          </div>

          <div className="col-span-12 md:col-span-4">
            <label htmlFor="rating" className="block text-md font-medium text-gray-700 mb-2">
              {t("label_rating")}
            </label>
            <input
              id="rating"
              name="rating"
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={formData.rating ?? ""}
              onChange={handleRatingChange}
              className={ESTILO_INPUT_GENERICO}
              disabled={isBusy}
            />
          </div>

          <div className="col-span-12 md:col-span-4">
            <label htmlFor="cantidadDisponiblePublico" className="block text-md font-medium text-gray-700 mb-2">
              {t("label_cantidad_disponible_publico")}
            </label>
            <input
              id="cantidadDisponiblePublico"
              name="cantidadDisponiblePublico"
              type="number"
              min="0"
              step="1"
              value={formData.cantidadDisponiblePublico ?? 0}
              onChange={handleIntegerChange}
              className={ESTILO_INPUT_GENERICO}
              disabled={isBusy}
            />
          </div>

          {formData.id === 0 &&
            <div className="col-span-12 md:col-span-4">
              <label htmlFor="cantidadDisponiblePrivado" className="block text-md font-medium text-gray-700 mb-2">
                {t("label_cantidad_disponible_privado")}
              </label>
              <input
                id="cantidadDisponiblePrivado"
                name="cantidadDisponiblePrivado"
                type="number"
                min="0"
                step="1"
                value={formData.cantidadDisponiblePrivado ?? 0}
                onChange={handleIntegerChange}
                className={ESTILO_INPUT_GENERICO}
                disabled={isBusy}
              />
            </div>
          }
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <p className="text-sm text-slate-500">
              {showAdvancedImages
                ? t("label_imagenes_avanzado_activo")
                : t("label_imagenes_avanzado_hint")}
            </p>
            <button
              type="button"
              onClick={() => setShowAdvancedImages((prev) => !prev)}
              disabled={isBusy}
              className="shrink-0 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-ct-primary-300 hover:bg-ct-primary-50/40 hover:text-ct-primary-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {showAdvancedImages
                ? t("btn_ocultar_imagenes_avanzado")
                : t("btn_imagenes_avanzado")}
            </button>
          </div>

          <div className={`grid grid-cols-1 gap-4 ${showAdvancedImages ? "lg:grid-cols-2" : ""}`}>
            {showAdvancedImages && (
              <ProductMainImageSection
                tipoImagenPrincipal={formData.tipoImagenPrincipal}
                imagenPrincipal={formData.imagenPrincipal}
                onTipoChange={(value) => updateFormField("tipoImagenPrincipal", value)}
                onImagenChange={(value) => updateFormField("imagenPrincipal", value)}
                t={t}
                disabled={isBusy}
                onBusyChange={setImageBusy}
                onError={(msg) => showToast(msg, "error")}
              />
            )}

            <ProductGallerySection
              tipoImagenes={formData.tipoImagenes}
              imagenes={formData.imagenes}
              onTipoChange={(value) => updateFormField("tipoImagenes", value)}
              onImagenesChange={(value) => updateFormField("imagenes", value)}
              t={t}
              disabled={isBusy}
              onBusyChange={setImageBusy}
              onError={(msg) => showToast(msg, "error")}
            />
          </div>
        </div>

        <FormActionsBar>
          <FormCancelButton
            label={t("btn_cancelar")}
            onClick={handleCancelClick}
            disabled={isBusy}
          />
          <FormSubmitButton
            loading={sendRequest}
            onClick={handleSaveClick}
            label={t("btn_guardar")}
            variant="primary"
            disabled={imageBusy}
          />
        </FormActionsBar>
      </div>
    </div>
  );
}
